import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  FaBriefcase, FaCheckCircle, FaTimesCircle, FaClock, 
  FaSearch, FaTrash, FaHandshake, FaMoneyBillWave, FaFilter 
} from 'react-icons/fa';

interface ReferralDeal {
  id: number;
  giver_member_id: number;
  receiver_member_id: number;
  giver_member_name: string;
  receiver_member_name: string;
  referred_person_name: string;
  referred_person_mobile: string;
  business_amount: number;
  business_date: string;
  notes?: string;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
  verified_by?: string;
  verified_at?: string;
  created_at: string;
}

interface ReferralBusinessAdminProps {
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
}

const ReferralBusinessAdmin: React.FC<ReferralBusinessAdminProps> = ({
  statusFilter: propStatusFilter,
  onStatusFilterChange,
  searchQuery: propSearchQuery,
  onSearchQueryChange
}) => {
  const [deals, setDeals] = useState<ReferralDeal[]>([]);
  const [summary, setSummary] = useState<any>({
    total_deals: 0,
    total_verified_amount: 0,
    total_pending_amount: 0,
    pending_count: 0,
    verified_count: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [internalStatusFilter, setInternalStatusFilter] = useState<string>('ALL');
  const [internalSearchQuery, setInternalSearchQuery] = useState<string>('');

  const activeStatusFilter = propStatusFilter !== undefined ? propStatusFilter : internalStatusFilter;
  const activeSearchQuery = propSearchQuery !== undefined ? propSearchQuery : internalSearchQuery;

  const handleStatusChange = (newStatus: string) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(newStatus);
    } else {
      setInternalStatusFilter(newStatus);
    }
  };

  const handleSearchChange = (query: string) => {
    if (onSearchQueryChange) {
      onSearchQueryChange(query);
    } else {
      setInternalSearchQuery(query);
    }
  };

  const fetchReferralDeals = async () => {
    setLoading(true);
    try {
      const [res, metricsRes] = await Promise.all([
        axios.get(`https://rmbbackend.rmbjaipur.com/auth/referral-business`),
        axios.get('https://rmbbackend.rmbjaipur.com/auth/dashboard-activity-metrics').catch(() => null)
      ]);

      if (res.data?.success) {
        setDeals(res.data.data || []);
      }

      if (metricsRes?.data && (metricsRes.data.total_activities_submitted > 0 || metricsRes.data.business_generated_amount > 0)) {
        const m = metricsRes.data;
        setSummary({
          total_deals: m.total_activities_submitted || res.data?.summary?.total_deals || 0,
          total_verified_amount: m.business_generated_amount !== undefined ? m.business_generated_amount : (res.data?.summary?.total_verified_amount || 0),
          total_pending_amount: m.pending_verification_amount !== undefined ? m.pending_verification_amount : (res.data?.summary?.total_pending_amount || 0),
          verified_count: m.total_verified_count !== undefined ? m.total_verified_count : (res.data?.summary?.verified_count || 0)
        });
      } else if (res.data?.summary) {
        setSummary(res.data.summary);
      }
    } catch (err) {
      console.error('Error fetching referral business deals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralDeals();
  }, []);

  const handleVerifyStatus = async (id: number, newStatus: 'VERIFIED' | 'REJECTED') => {
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/referral-business/${id}/verify`, {
        status: newStatus,
        verified_by: 'Super Admin'
      });
      alert(`🎉 Referral Business deal has been marked as ${newStatus}!`);
      fetchReferralDeals();
    } catch (err: any) {
      console.error('Error updating verification status:', err);
      alert(err.response?.data?.details || 'Failed to update deal status.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this referral business entry?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/referral-business/${id}`);
      alert('Referral business entry deleted.');
      fetchReferralDeals();
    } catch (err) {
      console.error('Error deleting referral deal:', err);
      alert('Failed to delete referral deal.');
    }
  };

  const filteredDeals = deals.filter(d => {
    // Status Filter
    const st = (d.status || 'PENDING').toUpperCase();
    if (activeStatusFilter !== 'ALL') {
      if (activeStatusFilter === 'PENDING' && st !== 'PENDING' && st !== '') return false;
      if (activeStatusFilter === 'VERIFIED' && st !== 'VERIFIED') return false;
      if (activeStatusFilter === 'REJECTED' && st !== 'REJECTED') return false;
      if (activeStatusFilter === 'DELETED') return false;
    }

    // Search Query Filter
    if (activeSearchQuery && activeSearchQuery.trim()) {
      const q = activeSearchQuery.toLowerCase().trim();
      const match = (
        (d.giver_member_name || '').toLowerCase().includes(q) ||
        (d.receiver_member_name || '').toLowerCase().includes(q) ||
        (d.referred_person_name || '').toLowerCase().includes(q) ||
        (d.referred_person_mobile || '').toLowerCase().includes(q) ||
        (d.notes || '').toLowerCase().includes(q) ||
        String(d.business_amount || '').includes(q)
      );
      if (!match) return false;
    }
    return true;
  });

  const getStatusBadge = (st: string) => {
    switch (st) {
      case 'VERIFIED':
        return <span className="bg-emerald-100 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/30 text-emerald-800 dark:text-emerald-400 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaCheckCircle /> Verified</span>;
      case 'REJECTED':
        return <span className="bg-rose-100 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 text-rose-800 dark:text-rose-400 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaTimesCircle /> Rejected</span>;
      default:
        return <span className="bg-amber-100 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 text-amber-800 dark:text-amber-400 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1 w-max"><FaClock /> Pending Verification</span>;
    }
  };

  return (
    <div className="p-4 sm:p-5 bg-white dark:bg-[#131E32] text-slate-800 dark:text-slate-100 space-y-5 rounded-2xl border border-slate-200 dark:border-[#2A3B5C] shadow-sm">
      
      {/* Header Banner */}
      <div className="bg-slate-50 dark:bg-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-3 shadow-sm">
        <div>
          <h1 className="text-lg sm:text-xl font-black text-[#0A2451] dark:text-white flex items-center gap-2">
            <FaBriefcase className="text-[#FDBC12]" /> Referral Business &amp; Closed Deals Verification
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Verify member referral deals, track closed business amounts, and approve business points.
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={fetchReferralDeals}
            className="w-full sm:w-auto px-4 py-2 bg-[#0A2451] hover:bg-blue-900 dark:bg-amber-500 dark:hover:bg-amber-400 text-xs font-bold text-white dark:text-slate-950 rounded-xl shadow-xs transition cursor-pointer flex items-center justify-center gap-1.5"
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1E2B45] p-4 rounded-2xl border border-slate-200 dark:border-[#2A3B5C] shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xl font-black">
            <FaBriefcase />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase">Total Activities Submitted</div>
            <div className="text-2xl font-black text-slate-900 dark:text-white">{summary.total_deals}</div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1E2B45] p-4 rounded-2xl border border-emerald-200 dark:border-emerald-500/30 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 text-xl font-black">
            <FaMoneyBillWave />
          </div>
          <div>
            <div className="text-[11px] text-emerald-700 dark:text-emerald-400 font-bold uppercase">Verified Closed Business</div>
            <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">₹{Number(summary.total_verified_amount).toLocaleString()}</div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1E2B45] p-4 rounded-2xl border border-amber-200 dark:border-amber-500/30 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-600 dark:text-amber-400 text-xl font-black">
            <FaClock />
          </div>
          <div>
            <div className="text-[11px] text-amber-700 dark:text-amber-400 font-bold uppercase">Pending Verification</div>
            <div className="text-2xl font-black text-amber-600 dark:text-amber-400">₹{Number(summary.total_pending_amount).toLocaleString()}</div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#1E2B45] p-4 rounded-2xl border border-purple-200 dark:border-purple-500/30 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xl font-black">
            <FaCheckCircle />
          </div>
          <div>
            <div className="text-[11px] text-purple-700 dark:text-purple-400 font-bold uppercase">Verified Ratio</div>
            <div className="text-2xl font-black text-purple-700 dark:text-purple-300">{summary.verified_count} / {summary.total_deals}</div>
          </div>
        </div>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="bg-slate-50 dark:bg-[#1E2B45] p-3.5 sm:p-4 rounded-xl border border-slate-200 dark:border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-80">
          <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
          <input
            type="text"
            placeholder="Search Giver, Receiver, or Referred Person..."
            value={activeSearchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white dark:bg-[#131E32] border border-slate-300 dark:border-[#2A3B5C] focus:border-[#0A2451] dark:focus:border-[#FDBC12] rounded-lg text-xs text-slate-900 dark:text-white outline-none font-bold shadow-xs"
          />
        </div>

        <div className="flex items-center gap-2 text-xs font-bold w-full sm:w-auto overflow-x-auto">
          <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1"><FaFilter /> Status:</span>
          {(['ALL', 'PENDING', 'VERIFIED', 'REJECTED'] as const).map(st => (
            <button
              key={st}
              onClick={() => handleStatusChange(st)}
              className={`px-3 py-1.5 rounded-lg transition cursor-pointer whitespace-nowrap ${
                activeStatusFilter === st
                  ? 'bg-[#0A2451] text-white dark:bg-[#FDBC12] dark:text-[#0A2451] font-black shadow-xs'
                  : 'bg-white dark:bg-[#131E32] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:text-white border border-slate-200 dark:border-[#2A3B5C]'
              }`}
            >
              {st === 'ALL' ? 'All Deals' : st}
            </button>
          ))}
        </div>
      </div>

      {/* Referral Business Data Table */}
      <div className="bg-white dark:bg-[#1E2B45] rounded-2xl border border-slate-200 dark:border-[#2A3B5C] overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-200 dark:border-[#2A3B5C] bg-slate-100 dark:bg-[#131E32] flex justify-between items-center">
          <h3 className="font-extrabold text-[#0A2451] dark:text-white text-sm flex items-center gap-2">
            <FaHandshake className="text-[#FDBC12]" /> Referral Business Submissions ({filteredDeals.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-slate-100 dark:bg-[#0B1220] text-slate-700 dark:text-slate-300 font-bold border-b border-slate-200 dark:border-[#2A3B5C] uppercase text-[11px]">
              <tr>
                <th className="p-3.5">Reference Provided By (Giver)</th>
                <th className="p-3.5">Receiver Member</th>
                <th className="p-3.5">Referred Person Name &amp; Mobile</th>
                <th className="p-3.5">Amount Closed</th>
                <th className="p-3.5">Date</th>
                <th className="p-3.5">Notes</th>
                <th className="p-3.5">Status</th>
                <th className="p-3.5 text-center">Admin Verification Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-[#2A3B5C] text-slate-800 dark:text-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400 font-medium">Loading referral business data...</td>
                </tr>
              ) : filteredDeals.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400 font-medium">No referral business entries found.</td>
                </tr>
              ) : (
                filteredDeals.map((deal) => (
                  <tr key={deal.id} className="hover:bg-slate-50 dark:hover:bg-[#131E32] transition">
                    <td className="p-3.5">
                      <div className="font-extrabold text-[#0A2451] dark:text-amber-400">{deal.giver_member_name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">ID: #{deal.giver_member_id}</div>
                    </td>
                    <td className="p-3.5">
                      <div className="font-extrabold text-blue-700 dark:text-blue-400">{deal.receiver_member_name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">ID: #{deal.receiver_member_id}</div>
                    </td>
                    <td className="p-3.5">
                      <div className="font-extrabold text-slate-900 dark:text-white">{deal.referred_person_name}</div>
                      {deal.referred_person_mobile && <div className="text-[10px] text-slate-500 dark:text-slate-400">📞 {deal.referred_person_mobile}</div>}
                    </td>
                    <td className="p-3.5 font-black text-emerald-600 dark:text-emerald-400 text-sm">₹{Number(deal.business_amount).toLocaleString()}</td>
                    <td className="p-3.5 text-slate-600 dark:text-slate-400">{deal.business_date ? new Date(deal.business_date).toLocaleDateString() : '-'}</td>
                    <td className="p-3.5 text-slate-600 dark:text-slate-300 max-w-xs truncate">{deal.notes || '-'}</td>
                    <td className="p-3.5">{getStatusBadge(deal.status)}</td>
                    <td className="p-3.5">
                      <div className="flex items-center justify-center gap-2">
                        {deal.status !== 'VERIFIED' && (
                          <button
                            onClick={() => handleVerifyStatus(deal.id, 'VERIFIED')}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded-lg shadow-xs transition cursor-pointer flex items-center gap-1"
                            title="Verify and Approve Business Deal"
                          >
                            <FaCheckCircle /> Verify
                          </button>
                        )}
                        {deal.status !== 'REJECTED' && (
                          <button
                            onClick={() => handleVerifyStatus(deal.id, 'REJECTED')}
                            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-[11px] rounded-lg shadow-xs transition cursor-pointer flex items-center gap-1"
                            title="Reject Business Deal"
                          >
                            <FaTimesCircle /> Reject
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(deal.id)}
                          className="p-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 rounded-lg transition cursor-pointer"
                          title="Delete Record"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReferralBusinessAdmin;
